import { useState, createContext, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const BookCallContext = createContext(null);

export const BookCallProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({ source: "", service: "" });

  const openDialog = useCallback((opts = {}) => {
    setMeta({ source: opts.source || "", service: opts.service || "Discovery call" });
    setOpen(true);
  }, []);

  return (
    <BookCallContext.Provider value={{ openDialog }}>
      {children}
      <BookCallDialog open={open} onOpenChange={setOpen} meta={meta} />
    </BookCallContext.Provider>
  );
};

export const useBookCall = () => {
  const ctx = useContext(BookCallContext);
  if (!ctx) throw new Error("useBookCall must be used within BookCallProvider");
  return ctx;
};

const BookCallDialog = ({ open, onOpenChange, meta }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setForm({ name: "", email: "", phone: "", notes: "" });
    setConsent(false);
    setDone(false);
    setSubmitting(false);
  };

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isValid =
    form.name.trim().length > 0 &&
    form.email.trim().length > 0 &&
    form.phone.trim().length > 0 &&
    consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      toast.error("Please fill all required fields and accept the data processing consent.");
      return;
    }
    setSubmitting(true);
    try {
      const sourceTag = meta.source ? ` [from: ${meta.source}]` : "";
      await api.post("/bookings", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: (form.notes || "") + sourceTag,
        service_type: meta.service || "Discovery call",
        apartment_address: "",
      });
      setDone(true);
      toast.success("Got it — we'll reply within 4 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 300);
      }}
    >
      <DialogContent className="bg-[#FAF9F6] border border-[#E5E2DC] max-w-md p-0 rounded-none max-h-[92vh] overflow-y-auto" data-testid="bookcall-dialog">
        {done ? (
          <div className="p-10 text-center" data-testid="bookcall-success">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#1C3326] mb-5" strokeWidth={1.2} />
            <p className="tag-line text-[#C26D5C] mb-2">Confirmed</p>
            <h2 className="font-serif text-3xl text-[#1A201C] mb-3">Talk soon.</h2>
            <p className="text-[#5C635A] text-sm leading-relaxed mb-8">
              We've received your request and will email <span className="font-medium text-[#1A201C]">{form.email}</span> within 4 business hours.
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6] w-full"
              data-testid="bookcall-close-btn"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 sm:p-10" data-testid="bookcall-form" noValidate>
            <DialogHeader className="text-left mb-6">
              <p className="tag-line text-[#C26D5C] mb-2">Free · English · We'll email you back</p>
              <DialogTitle className="font-serif text-3xl text-[#1A201C] tracking-tight leading-tight">
                Tell us about your move.
              </DialogTitle>
              <DialogDescription className="text-[#5C635A] text-sm pt-1">
                We'll email you back within 4 business hours with next steps.{" "}
                <span className="text-[#C26D5C]">*</span> = required field.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label className="tag-line text-[#1A201C]">
                  Name <span className="text-[#C26D5C]">*</span>
                </Label>
                <Input
                  required
                  value={form.name}
                  onChange={handle("name")}
                  className="mt-2 rounded-sm border-[#E5E2DC] bg-white"
                  data-testid="bookcall-name"
                />
              </div>
              <div>
                <Label className="tag-line text-[#1A201C]">
                  Email <span className="text-[#C26D5C]">*</span>
                </Label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={handle("email")}
                  className="mt-2 rounded-sm border-[#E5E2DC] bg-white"
                  data-testid="bookcall-email"
                />
              </div>
              <div>
                <Label className="tag-line text-[#1A201C]">
                  Phone <span className="text-[#C26D5C]">*</span>
                </Label>
                <Input
                  required
                  value={form.phone}
                  onChange={handle("phone")}
                  placeholder="+49 ..."
                  className="mt-2 rounded-sm border-[#E5E2DC] bg-white"
                  data-testid="bookcall-phone"
                />
              </div>
              <div>
                <Label className="tag-line text-[#1A201C]">Describe your situation</Label>
                <Textarea
                  rows={4}
                  value={form.notes}
                  onChange={handle("notes")}
                  placeholder="Moving to Munich next month, need help setting up a 2-bedroom apartment..."
                  className="mt-2 rounded-sm border-[#E5E2DC] bg-white"
                  data-testid="bookcall-notes"
                />
              </div>

              <label className="flex items-start gap-3 pt-2 cursor-pointer" data-testid="bookcall-consent-row">
                <Checkbox
                  checked={consent}
                  onCheckedChange={(v) => setConsent(v === true)}
                  className="mt-0.5 rounded-sm border-[#5C635A] data-[state=checked]:bg-[#1C3326] data-[state=checked]:border-[#1C3326]"
                  data-testid="bookcall-consent"
                />
                <span className="text-[11px] leading-relaxed text-[#5C635A]">
                  <span className="text-[#C26D5C]">*</span> I consent to the processing of my personal data (name, surname, email address, and phone number) by MUConcierge for the purpose of responding to my inquiry and contacting me. Further information can be found in the{" "}
                  <Link
                    to="/privacy"
                    target="_blank"
                    className="underline underline-offset-2 text-[#1C3326] hover:text-[#C26D5C]"
                    data-testid="bookcall-privacy-link"
                  >
                    Privacy Policy
                  </Link>
                  . I may revoke my consent at any time with future effect.
                </span>
              </label>
            </div>

            <Button
              type="submit"
              disabled={submitting || !isValid}
              className="w-full mt-7 rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] disabled:bg-[#5C635A]/30 disabled:text-[#5C635A] disabled:cursor-not-allowed text-[#FAF9F6] py-6 text-base"
              data-testid="bookcall-submit"
            >
              {submitting ? "Sending..." : "Send my request"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
